import React, { useMemo } from 'react'
import {
  Box,
  Card,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { com } from '@eidu/form'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { EntityPathNode } from './EntityPathUtils'
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath

export type RenderablePath = {
  entityPath: EntityPath
  entityPathLabel: string
  entityPathNodes: readonly EntityPathNode[]
}

const pathRenderComponentOuterStyle = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.7 }

const pathRenderComponentInnerStyle = { paddingX: 1.2, paddingY: 0.5 }

const PathRenderComponent: React.FC<{ path: RenderablePath }> = ({ path }) => (
  <Box sx={pathRenderComponentOuterStyle}>
    {path.entityPathNodes.map((node: EntityPathNode) => (
      <React.Fragment key={node.id}>
        {node.leftField && <KeyboardArrowLeftIcon fontSize="small" />}
        <Card variant="outlined">
          <Stack direction="row" spacing={1} alignItems="center" sx={pathRenderComponentInnerStyle}>
            {node.leftField && <Chip label={node.leftField} size="small" variant="filled" />}
            <Box>{node.type}</Box>
            {node.rightField && <Chip label={node.rightField} size="small" variant="filled" />}
          </Stack>
        </Card>
        {node.rightField && <KeyboardArrowRightIcon fontSize="small" />}
      </React.Fragment>
    ))}
  </Box>
)

type PathFieldProps = {
  paths: readonly RenderablePath[]
  selectedPath?: EntityPath
  onChange: (path?: EntityPath) => void
  disabled?: boolean
}

const PathField: React.FC<PathFieldProps> = ({ paths, selectedPath, onChange, disabled = false }) => {
  const hasError = useMemo(
    () =>
      !(selectedPath instanceof EntityPath) || paths.find((path) => path.entityPath.equals(selectedPath)) === undefined,
    [paths, selectedPath]
  )
  const handleChange = (event: SelectChangeEvent<number>) => {
    const index = Number(event.target.value)
    onChange(paths[index].entityPath)
  }
  if (paths.length === 0) {
    return (
      <Typography variant="body2" color="error">
        No paths found
      </Typography>
    )
  } else {
    return (
      <FormControl error={hasError} disabled={disabled}>
        <InputLabel id="entity-path-label">Path to target</InputLabel>
        <Select
          labelId="entity-path-label"
          label="Path to target"
          value={paths.findIndex((path) => path.entityPath.equals(selectedPath))}
          onChange={handleChange}
          disabled={disabled}
        >
          {paths.map((path, index) => (
            <MenuItem key={path.entityPathLabel} value={index}>
              <PathRenderComponent path={path} />
            </MenuItem>
          ))}
        </Select>
        {hasError && <FormHelperText error>Warning: Please select a path.</FormHelperText>}
      </FormControl>
    )
  }
}

export default PathField
